import { Bell, Check, Eye, Megaphone, Paperclip, Send } from "lucide-react";
import { useState } from "react";
import { useApp } from "../../domain/AppContext";
import { compareClassNames, formatClassCodesInText, formatClassName } from "../../domain/classCode";
import type { NotificationAudience } from "../../domain/types";
import { remoteRepository } from "../../services/remoteRepository";
import {
  Button,
  Dialog,
  Field,
  InlineMessage,
  PageHeader,
} from "../../components/ui";

export function CommunicationsPage() {
  const { profile, data, markAllNotificationsRead, createNotification, notify } = useApp();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(true);
  const [platform, setPlatform] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recipients, setRecipients] = useState<NotificationAudience[]>([]);
  const [audienceKind, setAudienceKind] = useState<NotificationAudience['kind']>('all_students');
  const [audienceValue, setAudienceValue] = useState('');
  const [specificIds, setSpecificIds] = useState<string[]>([]);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [attachment, setAttachment] = useState('');
  const [sendTime, setSendTime] = useState('now');
  const [sending, setSending] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  if (!data) return null;
  const markRead = async () => {
    const result = await markAllNotificationsRead();
    if (result.ok)
      notify({
        type: "success",
        title: result.data
          ? `${result.data} notificações marcadas como lidas`
          : "Nenhuma notificação pendente",
      });
  };
  const saveDraft = async () => {
    const result = await remoteRepository.saveApplicationRecord('communication_draft', 'composer', { title, message, recipients, email, platform, attachment, sendTime }, profile?.unitId);
    if (result.ok) setDraftSaved(true);
    notify(result.ok ? { type: 'success', title: 'Rascunho salvo na sua conta' } : { type: 'error', title: 'Rascunho não salvo', description: result.error.message });
  };
  const publish = async () => {
    if (!platform) return notify({ type: 'error', title: 'Selecione a publicação na plataforma', description: 'O envio por e-mail ainda não está disponível.' });
    if (!recipients.length) return notify({ type: 'error', title: 'Adicione ao menos um grupo de destinatários' });
    if (sendTime !== 'now') return notify({ type: 'info', title: 'Agendamento ainda indisponível', description: 'Escolha Enviar agora para publicar imediatamente na plataforma.' });
    setSending(true);
    const result = await createNotification({ title, description: message, category: 'Sistema', unitId: profile?.unitId, audience: recipients });
    setSending(false);
    if (!result.ok) return notify({ type: 'error', title: 'Comunicado não publicado', description: result.error.message });
    notify({ type: 'success', title: 'Comunicado publicado na plataforma', description: email ? 'A cópia por e-mail não foi enviada porque a integração externa ainda não está conectada.' : `${recipients.length} grupo(s) selecionado(s).` });
  };
  const classes = [...new Set(data.students.map((item) => item.className))].sort(compareClassNames);
  const visibleNotifications = data.notifications.filter((item) => item.category !== 'Materiais' && !`${item.title} ${item.description}`.toLocaleLowerCase('pt-BR').includes('material'));
  const specificPeople = audienceKind === 'specific_students' ? data.students : audienceKind === 'specific_teachers' ? data.teachers.filter((item) => !item.isSystemManager) : [];
  const visiblePeople = specificPeople.filter((item) => `${item.name} ${'ra' in item ? item.ra : item.email}`.toLocaleLowerCase('pt-BR').includes(recipientQuery.toLocaleLowerCase('pt-BR')));
  const addAudience = () => {
    let next: NotificationAudience | null = null;
    if (audienceKind === 'all_students') next = { kind: audienceKind, label: 'Todos os estudantes' };
    if (audienceKind === 'all_teachers') next = { kind: audienceKind, label: 'Todos os professores' };
    if (audienceKind === 'class' && audienceValue) next = { kind: audienceKind, className: audienceValue, label: `Turma ${audienceValue}` };
    if (audienceKind === 'olympiad' && audienceValue) { const olympiad = data.olympiads.find((item) => item.id === audienceValue); if (olympiad) next = { kind: audienceKind, olympiadId: olympiad.id, label: `Inscritos na ${olympiad.acronym}` }; }
    if ((audienceKind === 'specific_students' || audienceKind === 'specific_teachers') && specificIds.length) next = { kind: audienceKind, userIds: specificIds, label: `${specificIds.length} ${audienceKind === 'specific_students' ? 'estudante(s)' : 'professor(es)'} específico(s)` };
    if (!next) return notify({ type: 'error', title: 'Selecione os destinatários deste grupo' });
    const key = JSON.stringify(next);
    if (recipients.some((item) => JSON.stringify(item) === key)) return notify({ type: 'info', title: 'Esse grupo já foi adicionado' });
    setRecipients((items) => [...items, next]);
    setDraftSaved(false);
    setAudienceValue('');
    setSpecificIds([]);
    setRecipientQuery('');
  };
  return (
    <div className="communications-page">
      <PageHeader
        eyebrow="Comunicação"
        title="Comunicados e notificações"
        description="Mensagens com destinatários explícitos e pré-visualização antes do envio."
      />
      <div className="communication-layout">
        <main>
          <section className="composer">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Novo comunicado</p>
                <h2>Editor de mensagem</h2>
              </div>
              <span className={`draft-status${draftSaved ? '' : ' unsaved'}`}><Check size={13} aria-hidden="true" />{draftSaved ? 'Rascunho salvo' : 'Ainda não salvo'}</span>
            </div>
            <Field label="Título">
              <input
                value={title}
                onChange={(event) => { setTitle(event.target.value); setDraftSaved(false); }}
              />
            </Field>
            <Field label="Destinatários">
              <div className="recipient-chips">
                {recipients.length ? recipients.map((item, index) => <span key={`${item.kind}-${index}`}>{item.className ? `Turma ${formatClassName(item.className)}` : item.label} <button type="button" aria-label={`Remover ${item.className ? `Turma ${formatClassName(item.className)}` : item.label}`} onClick={() => { setRecipients((items) => items.filter((_, currentIndex) => currentIndex !== index)); setDraftSaved(false); }}>×</button></span>) : <small>Nenhum grupo adicionado</small>}
              </div>
              <div className="recipient-builder">
                <select aria-label="Tipo de agrupamento" value={audienceKind} onChange={(event) => { setAudienceKind(event.target.value as NotificationAudience['kind']); setAudienceValue(''); setSpecificIds([]); }}><option value="all_students">Todos os estudantes</option><option value="class">Estudantes de uma turma</option><option value="olympiad">Inscritos em uma olimpíada</option><option value="specific_students">Estudantes específicos</option><option value="all_teachers">Todos os professores</option><option value="specific_teachers">Professores específicos</option></select>
                {audienceKind === 'class' ? <select aria-label="Turma" value={audienceValue} onChange={(event) => setAudienceValue(event.target.value)}><option value="">Selecione a turma</option>{classes.map((item) => <option key={item} value={item}>{formatClassName(item)}</option>)}</select> : null}
                {audienceKind === 'olympiad' ? <select aria-label="Olimpíada" value={audienceValue} onChange={(event) => setAudienceValue(event.target.value)}><option value="">Selecione a olimpíada</option>{data.olympiads.map((item) => <option key={item.id} value={item.id}>{item.acronym} · {item.name}</option>)}</select> : null}
                {audienceKind === 'specific_students' || audienceKind === 'specific_teachers' ? <div className="specific-recipient-picker"><input aria-label="Buscar destinatário" placeholder={audienceKind === 'specific_students' ? 'Buscar por nome ou RA' : 'Buscar por nome ou e-mail'} value={recipientQuery} onChange={(event) => setRecipientQuery(event.target.value)} /><div>{visiblePeople.map((person) => <label key={person.id}><input type="checkbox" checked={specificIds.includes(person.id)} onChange={(event) => setSpecificIds((items) => event.target.checked ? [...items, person.id] : items.filter((id) => id !== person.id))} /><span><strong>{person.name}</strong><small>{'ra' in person ? `RA ${person.ra} · ${formatClassName(person.className)}` : person.email}</small></span></label>)}</div></div> : null}
                <Button variant="secondary" onClick={addAudience}>Adicionar grupo</Button>
              </div>
            </Field>
            <p className="recipient-count">
              {recipients.length} grupo(s) selecionado(s). A contagem final será calculada pelo sistema sem duplicar pessoas.
            </p>
            <Field label="Mensagem">
              <textarea
                rows={10}
                value={message}
                  onChange={(event) => { setMessage(event.target.value); setDraftSaved(false); }}
              />
            </Field>
            <label className="attachment-button"><Paperclip size={17} /> {attachment || 'Adicionar anexo'}<input className="sr-only" type="file" onChange={(event) => { setAttachment(event.target.files?.[0]?.name ?? ''); setDraftSaved(false); }} /></label>
            <Field label="Envio">
              <select value={sendTime} onChange={(event) => setSendTime(event.target.value)}>
                <option value="now">Enviar agora</option>
                <option value="schedule">Agendar envio</option>
              </select>
            </Field>
            <div className="send-channels">
              <label>
                <input
                  type="checkbox"
                  checked={platform}
                  onChange={(event) => setPlatform(event.target.checked)}
                />
                <span>
                  <Check size={14} />
                </span>
                Publicar na plataforma
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={email}
                  onChange={(event) => setEmail(event.target.checked)}
                />
                <span>
                  <Check size={14} />
                </span>
                Enviar por e-mail
              </label>
              <label>
                <input type="checkbox" disabled />
                Notificação push
              </label>
            </div>
            <div className="composer-actions">
              <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
                <Eye size={17} /> Pré-visualizar como estudante
              </Button>
              <Button
                variant="ghost"
                onClick={saveDraft}
              >
                Salvar rascunho
              </Button>
              <Button loading={sending} onClick={() => void publish()}>
                <Send size={17} /> {sendTime === 'now' ? 'Publicar comunicado' : 'Agendar envio'}
              </Button>
            </div>
          </section>
        </main>
        <aside className="notification-center">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Central</p>
              <h2>Notificações</h2>
            </div>
            <button type="button" onClick={markRead}>
              Marcar todas como lidas
            </button>
          </div>
          {visibleNotifications.map((item) => (
            <article key={item.id} className={!item.read ? "unread" : ""}>
              <span>
                <Bell size={16} />
              </span>
              <div>
                <strong>{formatClassCodesInText(item.title)}</strong>
                <p>{formatClassCodesInText(item.description)}</p>
                <small>
                  {item.createdAt} · {item.category}
                </small>
              </div>
            </article>
          ))}
        </aside>
      </div>
      <Dialog
        open={previewOpen}
        title="Pré-visualização como estudante"
        description="Esta é a aparência do comunicado antes do envio."
        onClose={() => setPreviewOpen(false)}
      >
        <article className="message-preview">
          <span>
            <Megaphone size={21} />
          </span>
          <div>
            <p className="eyebrow">Comunicado da escola</p>
            <h2>{formatClassCodesInText(title)}</h2>
            <p>{formatClassCodesInText(message)}</p>
            <small>Agora · Oficina Sapiens</small>
          </div>
        </article>
        <InlineMessage title="Nenhuma mensagem externa será enviada">
          O envio por e-mail será liberado após a configuração institucional.
        </InlineMessage>
      </Dialog>
    </div>
  );
}
